import { Box, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';

import useSnackBar from '../../hooks/useSnackBar/useSnackBar';
import useDate from '../../hooks/useDate/useDate';

import { setBulkFilter } from '../../stores/TransactionsStore';
import { RootState } from '../../stores/Store';

import { Header, Row } from '../../components/Table/TableModels';
import ClickableLink from '../../components/Text/ClickableLink';
import TruncatedTooltip from '../../components/TruncatedTooltip/TruncatedTooltip';
import Tooltip from '../../components/Tooltip/Tooltip';

import { Sort } from '../../models/CommonModels';
import {
  BulkTransactionList,
  TransactionList,
  TransactionsListRequest,
  TransactionStatus,
  TransactionType,
} from '../../models/TransactionsModels';

import TransactionsService from '../../services/Transactions/TransactionsService';
import { FileFormat } from '../../models/ImportModels';

const singleHeaders = ['creationDate', 'status', 'userFullName', 'userEmail', 'serialNumber', 'eid'];
const bulkHeaders = [
  'creationDate',
  'totalConnections',
  'processingConnections',
  'completedConnections',
  'failedConnections',
  'viewDetails',
];

function useTransactionsTable(activePage: number, pageSize: number, sort: Sort) {
  const [t] = useTranslation();
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [tableHeaders, setTableHeaders] = useState<string[]>([]);
  const [transactions, setTransactions] = useState<TransactionList[]>([]);
  const [bulkTransactions, setBulkTransactions] = useState<BulkTransactionList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const snackBar = useSnackBar();
  const { localeDateFormat, localeDateTimeFormat } = useDate();
  const theme = useTheme();
  const { filter } = useSelector((state: RootState) => state.transactionsReducer);
  const dispatch = useDispatch();

  /**
   * Fetch transactions request
   */
  const fetchTransactions = () => {
    if (!filter.type) return;

    setIsLoading(true);
    const requestParams: TransactionsListRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order,
      filter:
        filter.type !== TransactionType.BULK_NEW_CONNECTION && filter.type !== TransactionType.BULK_SIM_SWAP
          ? filter.filter
          : '',
      journey: filter.type,
      statusList: filter.status ? [filter.status] : undefined,
    };

    TransactionsService.api.fetchTransactions(requestParams).then(
      (response) => {
        setTransactions(response.data.transactions || []);
        setBulkTransactions(response.data.bulkTransactions || []);
        setTotalTransactions(response.data.totalElements || 0);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.transactions.fetchTransactionsError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Exports transactions
   */
  const exportData = (format: FileFormat) => {
    setIsLoading(true);
    const requestParams: TransactionsListRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order.toUpperCase(),
      filter:
        filter.type !== TransactionType.BULK_NEW_CONNECTION && filter.type !== TransactionType.BULK_SIM_SWAP
          ? filter.filter
          : '',
      journey: filter.type ? filter.type : undefined,
      statusList: filter.status ? [filter.status] : undefined,
    };

    TransactionsService.api
      .exportData(
        requestParams,
        format,
        `requests_${filter.type.toLowerCase()}_${DateTime.now().toFormat(localeDateTimeFormat)}.${format}`,
      )
      .catch(() => {
        snackBar.showSnackBar(t('pages.transactions.exportFileError'), 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  React.useEffect(() => {
    fetchTransactions();
  }, [activePage, pageSize, filter, sort]);

  React.useEffect(() => {
    if (!filter.type) return;

    switch (filter.type) {
      case TransactionType.BULK_NEW_CONNECTION:
      case TransactionType.BULK_SIM_SWAP:
        setTableHeaders(bulkHeaders);
        break;
      case TransactionType.NEW_CONNECTION:
      case TransactionType.SIM_SWAP:
      case TransactionType.SUSPEND:
      case TransactionType.RESTORE:
      case TransactionType.TERMINATION:
        setTableHeaders(singleHeaders);
        break;
      default:
        setTableHeaders([]);
        break;
    }
  }, [filter.type]);

  const headers: Header[] = useMemo(
    () =>
      tableHeaders.map((item) => ({
        id: item,
        label: t(`pages.transactions.table.headers.${item}`),
        isSortable: true,
        sx: {
          ...(item === 'viewDetails' && {
            position: 'sticky',
            right: 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: 1,
              boxShadow: '-8px 0px 16px 0px #00000029',
              opacity: 'var(--sticky-actions-shadow, 0)',
              pointerEvents: 'none',
              transition: 'opacity 120ms ease',
            },
          }),
        },
      })),
    [t, tableHeaders],
  );

  /**
   * Sets the rows for bulk transactions
   */
  const setBulkTransactionsRows = (): Row[] =>
    bulkTransactions?.map((transaction, index) => ({
      id: transaction.bulkId,
      cells: [
        {
          value: DateTime.fromISO(transaction.creationDate).toFormat(localeDateFormat),
        },
        {
          value: transaction.totalConnections,
        },
        {
          value: (
            <ClickableLink
              onClick={() => {
                sessionStorage.setItem('navigatingToDetails', 'true');
                dispatch(setBulkFilter({ filter: '', type: '', status: TransactionStatus.PROCESSING }));
              }}
              to={`/transactions/${transaction.bulkId}`}
            >
              {transaction.processingConnections}
            </ClickableLink>
          ),
        },
        {
          value: (
            <ClickableLink
              onClick={() => {
                sessionStorage.setItem('navigatingToDetails', 'true');
                dispatch(setBulkFilter({ filter: '', type: '', status: TransactionStatus.COMPLETED }));
              }}
              to={`/transactions/${transaction.bulkId}`}
            >
              {transaction.completedConnections}
            </ClickableLink>
          ),
        },
        {
          value: (
            <ClickableLink
              onClick={() => {
                sessionStorage.setItem('navigatingToDetails', 'true');
                dispatch(setBulkFilter({ filter: '', type: '', status: TransactionStatus.ERROR }));
              }}
              to={`/transactions/${transaction.bulkId}`}
            >
              {transaction.failedConnections}
            </ClickableLink>
          ),
        },
        {
          value: (
            <Box
              sx={{
                height: '100%',
                padding: '0.688rem 1rem',
              }}
            >
              <ClickableLink
                onClick={() => sessionStorage.setItem('navigatingToDetails', 'true')}
                to={`/transactions/${transaction.bulkId}`}
              >
                {t('common.table.viewDetails')}
              </ClickableLink>
            </Box>
          ),
          sx: {
            position: 'sticky',
            right: 0,
            p: 0,
            backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: 1,
              boxShadow: '-8px 0px 16px 0px #00000029',
              opacity: 'var(--sticky-actions-shadow, 0)',
              pointerEvents: 'none',
              transition: 'opacity 120ms ease',
            },
          },
        },
      ],
    }));

  /**
   * Sets the rows for single transactions
   */
  const setSingleTransactionsRows = (): Row[] =>
    transactions?.map((transaction) => ({
      id: transaction.actionId,
      cells: [
        {
          value: transaction.creationDate ? DateTime.fromISO(transaction.creationDate).toFormat(localeDateFormat) : '',
        },
        {
          value: transaction.status ? (
            <Tooltip
              label={t(`pages.transactions.table.status.${transaction.status}`)}
              message={
                transaction.status === TransactionStatus.ERROR ? t(`pages.transactions.table.errorStatusTooltip`) : ''
              }
              sx={{ width: '12.125rem' }}
            />
          ) : (
            ''
          ),
        },
        {
          value: transaction.userFullName,
        },
        {
          value: transaction.userEmail,
        },
        {
          value: transaction.serialNumber ? <TruncatedTooltip label={transaction.serialNumber} limit={4} /> : '',
        },
        {
          value: transaction.eid ? <TruncatedTooltip label={transaction.eid} limit={4} /> : '',
        },
      ],
    }));

  const rows: Row[] = useMemo(() => {
    switch (filter.type) {
      case TransactionType.BULK_NEW_CONNECTION:
      case TransactionType.BULK_SIM_SWAP:
        return setBulkTransactionsRows();
      case TransactionType.NEW_CONNECTION:
      case TransactionType.SIM_SWAP:
      case TransactionType.SUSPEND:
      case TransactionType.RESTORE:
      case TransactionType.TERMINATION:
        return setSingleTransactionsRows();
      default:
        return [];
    }
  }, [t, transactions]);

  return { headers, rows, totalTransactions, isLoading, fetchTransactions, exportData };
}

export default useTransactionsTable;
